/**
 * Amendment follow-source API client (ST-00212; server: server/routes/amendmentFollowSource.ts,
 * mounted under /api/amendments/v3). Shapes: `types/amendmentFollowSource`.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh). Resolves to the response BODY.
 *
 *   getSourceUpdates({adopted, acknowledged, limit, offset}) → SourceUpdatesResponse
 *   acknowledgeSourceUpdate(eventId)                          → { message, eventId }  (amendments.edit)
 *   getUnmatched({reason, limit, offset})                     → UnmatchedResponse
 *   attachUnmatched(anchorId, ticketUID)                      → ReviewActionResponse  (amendments.edit; 409 refused)
 *   archiveUnmatched(anchorId, notes?)                        → ReviewActionResponse  (amendments.edit)
 *   getFollowSourceRuns(limit?)                               → FollowSourceRunsResponse (settings.manage)
 *   runFollowSourceDryRun()                                   → FollowSourceRunSummary   (settings.manage; 409 busy)
 *
 * `followSourceErrorText(err, fallback)` turns a rejection into a sentence for the user (the
 * server's `{ message }` for a 409 refusal, a permission line for 403).
 */
import api from './api';
import type {
  ArchiveUnmatchedBody,
  AttachUnmatchedBody,
  FollowSourceRunSummary,
  FollowSourceRunsResponse,
  ReviewActionResponse,
  ReviewReason,
  SourceUpdatesResponse,
  UnmatchedResponse,
} from '../types/amendmentFollowSource';

const BASE = '/amendments/v3';
/** A dry run reads every anchored correction and the source (≈ 5–30 s on the prod copy). */
const DRY_RUN_TIMEOUT = 180000;
const LIST_TIMEOUT = 60000;

/** amendment_user of the versions the follow-source job writes (server jobs/amendmentFollowSource ACTOR). */
export const SOURCE_UPDATE_USER = 'Source update';

export interface SourceUpdatesParams {
  /** Include corrections the source now agrees with (adopted). */
  adopted?: boolean;
  /** Include acknowledged rows. */
  acknowledged?: boolean;
  limit?: number;
  offset?: number;
}

export interface UnmatchedParams {
  /** Only this reason; omitted = every reason. */
  reason?: ReviewReason | null;
  limit?: number;
  offset?: number;
}

export interface AcknowledgeResponse {
  message: string;
  eventId: number;
}

interface ApiErrorLike {
  response?: { status?: number; data?: unknown };
  message?: string;
}

/** The HTTP status of a rejected request, else null. */
export function errorStatus(err: unknown): number | null {
  return (err as ApiErrorLike | null)?.response?.status ?? null;
}

/** A sentence for the user: the API's message, else `fallback` (403 has its own words). */
export function followSourceErrorText(err: unknown, fallback: string): string {
  const e = err as ApiErrorLike | null;
  if (e?.response?.status === 403) return 'You do not have permission for this.';
  const data = e?.response?.data;
  const message =
    data && typeof data === 'object' ? (data as { message?: unknown }).message : undefined;
  if (typeof message === 'string' && message.trim()) return message;
  if (e?.message && /timeout/i.test(e.message)) return 'This took too long. Try again in a minute.';
  return fallback;
}

const flag = (on: boolean | undefined): string | undefined => (on ? '1' : undefined);

export async function getSourceUpdates(
  params: SourceUpdatesParams = {},
  config: { signal?: AbortSignal } = {}
): Promise<SourceUpdatesResponse> {
  const res = await api.get<SourceUpdatesResponse>(`${BASE}/source-updates`, {
    params: {
      adopted: flag(params.adopted),
      acknowledged: flag(params.acknowledged),
      limit: params.limit,
      offset: params.offset,
    },
    timeout: LIST_TIMEOUT,
    signal: config.signal,
  });
  return res.data;
}

export async function acknowledgeSourceUpdate(eventId: number): Promise<AcknowledgeResponse> {
  const res = await api.post<AcknowledgeResponse>(`${BASE}/source-updates/${eventId}/ack`);
  return res.data;
}

export async function getUnmatched(
  params: UnmatchedParams = {},
  config: { signal?: AbortSignal } = {}
): Promise<UnmatchedResponse> {
  const res = await api.get<UnmatchedResponse>(`${BASE}/unmatched`, {
    params: { reason: params.reason || undefined, limit: params.limit, offset: params.offset },
    timeout: LIST_TIMEOUT,
    signal: config.signal,
  });
  return res.data;
}

export async function attachUnmatched(
  anchorId: number,
  ticketUID: string
): Promise<ReviewActionResponse> {
  const body: AttachUnmatchedBody = { ticketUID };
  const res = await api.post<ReviewActionResponse>(`${BASE}/unmatched/${anchorId}/attach`, body);
  return res.data;
}

export async function archiveUnmatched(
  anchorId: number,
  notes?: string
): Promise<ReviewActionResponse> {
  const body: ArchiveUnmatchedBody = notes && notes.trim() ? { notes: notes.trim() } : {};
  const res = await api.post<ReviewActionResponse>(`${BASE}/unmatched/${anchorId}/archive`, body);
  return res.data;
}

export async function getFollowSourceRuns(limit?: number): Promise<FollowSourceRunsResponse> {
  const res = await api.get<FollowSourceRunsResponse>(`${BASE}/follow-source/runs`, {
    params: { limit },
  });
  return res.data;
}

export async function runFollowSourceDryRun(): Promise<FollowSourceRunSummary> {
  const res = await api.post<FollowSourceRunSummary>(`${BASE}/follow-source/dry-run`, undefined, {
    timeout: DRY_RUN_TIMEOUT,
  });
  return res.data;
}
